import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import '../../i18n';

// Client-facing consent page for the WiseClean x Aule Labs recording
// programme (EN/ET). Support sends the link from the admin Support AI
// interface (same flow as the payment link); the client joins with the
// video/audio toggles or declines, and the decision is stored as a
// Consent Record in ws1 (POST /data_consent). :orderId is the Airtable
// order recid — the endpoint resolves the client from the order. Full
// terms open in a new tab as a static PDF served from public/.
const API = 'https://api.wiseclean.ee/api:56Z3lZ-0';
const TERMS_URL = '/data-consent-terms.pdf';

const C = {
  bg: '#E8F7FA', card: '#FFFFFF', cyan: '#6DCBDE', cyanDark: '#1A7D8E',
  text: '#1A1A2E', muted: '#6B7280', border: '#E2E8F0', error: '#EF4444',
};
const S = {
  page: { minHeight: '100vh', background: C.bg, display: 'flex', justifyContent: 'center', alignItems: 'flex-start', padding: '24px', fontFamily: 'DM Sans, Arial, sans-serif', color: C.text },
  card: { background: C.card, borderRadius: 16, padding: '28px 24px', maxWidth: 460, width: '100%', boxShadow: '0 4px 24px rgba(0,0,0,0.06)' },
  h1: { fontSize: 22, fontWeight: 700, margin: '0 0 14px' },
  body: { fontSize: 15, lineHeight: 1.55, whiteSpace: 'pre-line', color: C.text },
  toggles: { marginTop: 16 },
  toggleRow: { display: 'flex', alignItems: 'center', gap: 10, marginTop: 10, fontSize: 15, cursor: 'pointer' },
  checkbox: { width: 20, height: 20, accentColor: '#6DCBDE', flexShrink: 0, cursor: 'pointer' },
  hint: { fontSize: 13, color: '#6B7280', marginTop: 8 },
  more: { display: 'inline-block', marginTop: 14, color: '#1A7D8E', fontSize: 14, textDecoration: 'none' },
  agree: { width: '100%', padding: '14px', borderRadius: 8, border: 'none', background: '#6DCBDE', color: '#fff', fontSize: 16, fontWeight: 600, cursor: 'pointer', marginTop: 18 },
  decline: { width: '100%', padding: '12px', borderRadius: 8, border: '1px solid #E2E8F0', background: '#fff', color: '#6B7280', fontSize: 14, cursor: 'pointer', marginTop: 10 },
  disabled: { opacity: 0.6, cursor: 'default' },
  err: { color: '#EF4444', fontSize: 14, marginTop: 10 },
};

export default function DataConsent() {
  const { lang, orderId } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [state, setState] = useState('form'); // form|thanks|declined
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState('');
  const [video, setVideo] = useState(true);
  const [audio, setAudio] = useState(true);

  useEffect(() => {
    if (['en', 'et'].includes(lang)) i18n.changeLanguage(lang);
    else navigate(`/internal/data-consent/en/${orderId}`, { replace: true });
  }, [lang, orderId, i18n, navigate]);

  async function respond(action) {
    setSubmitting(true);
    setErr('');
    try {
      const r = await fetch(`${API}/data_consent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          orderID: orderId,
          action,
          video: action === 'accepted' ? video : false,
          audio: action === 'accepted' ? audio : false,
          locale: lang,
          ua: typeof navigator !== 'undefined' ? navigator.userAgent : '',
        }),
      });
      if (!r.ok) throw new Error(t('dataConsentError'));
      setState(action === 'accepted' ? 'thanks' : 'declined');
    } catch (e) {
      setErr(e.message);
    } finally {
      setSubmitting(false);
    }
  }

  let content;
  if (state === 'thanks') {
    content = (
      <>
        <h1 style={S.h1}>{t('dataConsentThanksTitle')}</h1>
        <p style={{ color: C.muted }}>{t('dataConsentThanksBody')}</p>
      </>
    );
  } else if (state === 'declined') {
    content = (
      <>
        <h1 style={S.h1}>{t('dataConsentDeclinedTitle')}</h1>
        <p style={{ color: C.muted }}>{t('dataConsentDeclinedBody')}</p>
      </>
    );
  } else {
    content = (
      <>
        <h1 style={S.h1}>{t('dataConsentTitle')}</h1>
        <p style={S.body}>{t('dataConsentBody')}</p>
        <div style={S.toggles}>
          <label style={S.toggleRow}>
            <input type="checkbox" checked={video} onChange={(e) => setVideo(e.target.checked)} style={S.checkbox} />
            <span>{t('dataConsentVideo')}</span>
          </label>
          <label style={S.toggleRow}>
            <input type="checkbox" checked={audio} onChange={(e) => setAudio(e.target.checked)} style={S.checkbox} />
            <span>{t('dataConsentAudio')}</span>
          </label>
          {!video && <div style={S.hint}>{t('dataConsentNoVideoHint')}</div>}
        </div>
        <div>
          <a style={S.more} href={TERMS_URL} target="_blank" rel="noreferrer">{t('dataConsentTerms')} →</a>
        </div>
        {err && <div style={S.err}>{err}</div>}
        <button style={{ ...S.agree, ...(submitting ? S.disabled : {}) }} onClick={() => respond('accepted')} disabled={submitting}>
          {t('dataConsentAgree')}
        </button>
        <button style={{ ...S.decline, ...(submitting ? S.disabled : {}) }} onClick={() => respond('declined')} disabled={submitting}>
          {t('dataConsentDecline')}
        </button>
      </>
    );
  }

  return (
    <div style={S.page}>
      <div style={S.card}>{content}</div>
    </div>
  );
}
